import { useContext, useRef, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Button,
  FormControl,
  FormLabel,
  Box,
  useToast,
  Select,
} from "@chakra-ui/react";

import SectionHeading from "../SectionHeader";
import SectionDescription from "../SectionDescription";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { AlertsContext } from "providers/alerts";
import { apiRequest } from "utils/api";
import { DrawerFormField } from "pages/Borrowers/elements/AddBorrower";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";
import { useRoles } from "hooks/useRoles";

export default function InviteMember({
  roles,
  onLoad,
}: {
  roles: any[] | undefined;
  onLoad: () => void;
}) {
  const { showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const toast = useToast();
  const [sending, setSending] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [role, setRole] = useState<string>();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);

  // Fetch both lender and borrower roles
  const { roles: lenderRoles } = useRoles({
    userType: "lender",
    company_id: user.company.id,
  });

  // Combine both role types
  const allRoles = [...(lenderRoles || [])];

  async function sendInvite() {
    try {
      setSending(true);

      await apiRequest("/supabase/invite", {
        first_name: firstName,
        last_name: lastName,
        email,
        company: user.company.id,
        type: "lender",
        role: role,
        job_title: jobTitle,
      });
      onClose();
      onLoad();
      setFirstName("");
      setLastName("");
      setEmail("");
      setJobTitle("");
      setRole(undefined);
      toast({
        title: "Invite Sent!",
        description: "Your team member has been sent an email invite.",
      });
    } catch (err) {
      console.error(err);
      showError({
        title: "Invite Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <Box mb="20px">
      <SectionHeading title="Invite Members" />
      <SectionDescription text="Send account invites to different members of your team." />
      <Button ref={btnRef} variant={getCustomButtonVariant(user)} onClick={onOpen}>
        Invite Members
      </Button>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Invite Member</DrawerHeader>
          <DrawerBody>
            <DrawerFormField
              label="First Name"
              placeholder="John"
              value={firstName}
              onChange={setFirstName}
            />
            <DrawerFormField
              label="Last Name"
              placeholder="Doe"
              value={lastName}
              onChange={setLastName}
            />
            <DrawerFormField
              label="Email Address"
              placeholder="john@doe.com"
              value={email}
              onChange={setEmail}
              type="email"
            />
            <DrawerFormField
              label="Job Title"
              placeholder="Chief Lendtech Officer"
              value={jobTitle}
              onChange={setJobTitle}
              type="text"
            />

            <FormControl mb="28px" isRequired>
              <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                Permissions Role
              </FormLabel>
              <Select
                placeholder="Select role"
                onChange={e => setRole(e.target.value)}
                value={role}
              >
                {allRoles?.map((role: any) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={sendInvite}
              variant="solid"
              flex="1"
              isLoading={sending}
              loadingText="Sending"
            >
              Send Invite
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
